import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export interface BlockUserInput {
  id: string;
  value: string | Array<string> | null;
}

export interface PlainTextTableMap {
  [tableId: string]: {
    plainTextTable: Array<Array<string>>;
    dataFrameName: string;
  };
}

export interface CanvasSubmitParams {
  buttonId: string;
  blockUserInputs: Array<BlockUserInput>;
  plainTextTableMap: PlainTextTableMap;
}

export interface CanvasSubmitResponse {
  deliveryStatus: 'success' | 'failure';
}

export function submitCanvas(canvasId: string, params: CanvasSubmitParams): Promise<CanvasSubmitResponse> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/benchling-app-canvases/${canvasId}/interaction`,
    data: params,
  });
}

export interface NoteLinkedAppCanvasCreateParams {
  featureDeveloperDefinedId: string;
  appInstallationId: string;
}

/** Celery task handle returned when the server enqueues webhook polling (same shape as other TaskRunner APIs). */
export interface NoteLinkedAppCanvasCreateTaskResponse {
  status_url: string;
}

export interface NoteLinkedAppCanvasCreateResponse {
  deliveryStatus: 'success' | 'failure';
}

/**
 * Creates a note-linked app canvas. Uses a plain POST first; if the response includes `status_url`,
 * polls the task until completion (same behavior as TaskRunner.postJSONPromise). If the server has
 * nothing to enqueue (e.g. canvas already existed), the body is `{}` and this resolves to success
 * without polling.
 */
export async function noteLinkedAppCanvasCreate(
  entryId: string,
  provenanceUuid: string,
  params: NoteLinkedAppCanvasCreateParams
): Promise<NoteLinkedAppCanvasCreateResponse> {
  const initial = await net.postJSONPromise({
    url: `/1/api/note-linked-app-canvases/${entryId}/${provenanceUuid}:create-canvas`,
    data: params,
  });
  const maybeTask = initial as NoteLinkedAppCanvasCreateTaskResponse | Record<string, unknown> | null;
  if (maybeTask != null && typeof maybeTask === 'object' && typeof maybeTask.status_url === 'string') {
    return TaskRunner.resumeTaskPromise(maybeTask.status_url);
  }
  return {deliveryStatus: 'success'};
}

export interface HomepageAppCanvasCreateParams {
  featureDeveloperDefinedId: string;
  appInstallationId: string;
}

export interface HomepageAppCanvasCreateResponse {
  deliveryStatus: 'success' | 'failure';
}

export function homepageAppCanvasCreate(
  appId: string,
  params: HomepageAppCanvasCreateParams
): Promise<HomepageAppCanvasCreateResponse> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/homepage-app-canvases/${appId}:create-canvas`,
    data: params,
  });
}

export interface CanvasResetResponse {
  deliveryStatus: 'success' | 'failure';
}

export function resetCanvas(canvasId: string): Promise<CanvasResetResponse> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/benchling-app-canvases/${canvasId}:reset`,
  });
}
